//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import { useAuth } from 'contexts/AuthProvider';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { getClientId } from 'utils/clientId';

export type TriggerAction = 'reset' | 'upgrade';

/*
 * 歸因訊息的共用樣式。結尾的空格用來與後續本文分隔(行內情境),不可省略。
 */
export const TriggerText = ({ children }: { children: ReactNode }) => <span className="font-bold italic text-toast-warning-800">{children} </span>;

/* 依 triggerUser / triggerClientId 判斷觸發來源,供各流程的歸因訊息共用 */
export const classifyTrigger = (triggerUser?: string, triggerClientId?: string, userEmail?: string) => ({
  isSameUserOtherDevice: !!triggerUser && triggerUser === userEmail && !!triggerClientId && triggerClientId !== getClientId(),
  isOtherUser: !!triggerUser && triggerUser !== userEmail,
});

/*
 * reset / upgrade 的互斥型歸因訊息(兩者版型相同,只差 i18n key 前綴)。
 * i18n key 由 action 組成,實際使用到的 key 列於此以便全文搜尋:
 *   modal.resetTriggerSameUserMsg   / modal.resetTriggerOtherUserMsg
 *   modal.upgradeTriggerSameUserMsg / modal.upgradeTriggerOtherUserMsg
 */
export const TriggerAttributionMessage = ({
  action,
  triggerUser,
  triggerClientId,
}: {
  action: TriggerAction;
  triggerUser?: string;
  triggerClientId?: string;
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isSameUserOtherDevice, isOtherUser } = classifyTrigger(triggerUser, triggerClientId, user.email);

  if (isSameUserOtherDevice) return <TriggerText>{t(`modal.${action}TriggerSameUserMsg`)}</TriggerText>;
  if (isOtherUser) return <TriggerText>{t(`modal.${action}TriggerOtherUserMsg`, { triggerUser })}</TriggerText>;
  return null;
};
